import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import type { SharePublicView, UserRole } from '@xdocs/schemas';
import { Avatar, Badge, Chip, cn, useI18n } from '@xdocs/ui';
import { ManuscriptBodyHtml } from '../reader/index.js';
import { resolveCategoryLabel } from '../search/categoryLabel.js';
import { CommentThread } from './CommentThread.js';
import { ReactionBar } from './ReactionBar.js';
import { TakedownDialog } from './TakedownDialog.js';
import { useSharingT } from './useSharingT.js';
import styles from './PublicShareReader.module.css';

export interface PublicShareReaderProps {
  view: SharePublicView;
  /** `null` for an anonymous visitor — F63/F66 AC: link/public reads need
   * no account. */
  viewerUserId: string | null;
  viewerRole: UserRole | null;
  /** Omit on the `/s/:token` link reader — a link share is never indexed,
   * so "back to Discover" would be a dead end there. */
  backTo?: { label: string; to: string };
  /** Route to the full F65/F66 reader (owned by a separate workstream —
   * this component renders only the first chapter as an excerpt). */
  fullReaderTo?: string;
  onTakenDown?: () => void;
}

const MAX_EXCERPT_CHARS = 1200;

function excerptOf(body: string): string {
  if (body.length <= MAX_EXCERPT_CHARS) return body;
  return `${body.slice(0, MAX_EXCERPT_CHARS).trimEnd()}…`;
}

/**
 * The public read surface for a share (F62 `/discover/:shareId`, F63
 * `/s/:token`) — cover, owner, categories, reactions, an EXCERPT of the
 * manuscript (full pagination/fonts/themes is F66's own reader, a separate
 * workstream at `/read/:docId` — `fullReaderTo` links out to it rather than
 * re-implementing it here), and the F28/F29/F69 comment thread.
 */
export function PublicShareReader({ view, viewerUserId, viewerRole, backTo, fullReaderTo, onTakenDown }: PublicShareReaderProps): ReactElement {
  const { t } = useSharingT();
  const { t: tGlobal } = useI18n();
  const [takedownOpen, setTakedownOpen] = useState(false);
  const canModerate = viewerRole === 'moderator' || viewerRole === 'admin';
  const firstChapter = view.currentVersion.contentJsonb.chapters[0];
  const primaryCategory = view.categories[0];

  return (
    <article className={cn(styles['page'])}>
      {backTo && (
        <Link to={backTo.to} className={cn(styles['backLink'])}>
          <ChevronLeft size={14} aria-hidden="true" />
          {backTo.label}
        </Link>
      )}

      {canModerate && (
        <div className={cn(styles['moderatorBanner'])} role="status">
          <Badge tone="info">{t('sharing:moderator.badge')}</Badge>
          <span className={cn(styles['moderatorText'])}>{t('sharing:moderator.viewingAs')}</span>
          <button type="button" className={cn(styles['takedownButton'])} onClick={() => setTakedownOpen(true)}>
            {t('sharing:moderator.takedown')}
          </button>
        </div>
      )}

      <div className={cn(styles['cover'])}>
        {primaryCategory && <span className={cn(styles['coverLabel'])}>{resolveCategoryLabel(tGlobal, primaryCategory)}</span>}
      </div>

      <h1 className={cn(styles['title'])}>{view.title}</h1>

      <div className={cn(styles['metaRow'])}>
        <div className={cn(styles['owner'])}>
          <Avatar name={view.ownerDisplayName ?? '?'} size={34} />
          <span className={cn(styles['ownerName'])}>{view.ownerDisplayName ?? '—'}</span>
        </div>
        <div className={cn(styles['actionsRow'])}>
          <ReactionBar
            shareId={view.share.id}
            likeCount={view.likeCount}
            dislikeCount={view.dislikeCount}
            viewerReaction={view.viewerReaction}
            canReact={viewerUserId !== null}
          />
          {fullReaderTo && (
            <Link to={fullReaderTo} className={cn(styles['readButton'])}>
              {t('sharing:reader.readFull')}
            </Link>
          )}
        </div>
      </div>

      {view.categories.length > 0 && (
        <div className={cn(styles['categories'])}>
          {view.categories.map((category) => (
            <Chip key={category.id}>{resolveCategoryLabel(tGlobal, category)}</Chip>
          ))}
        </div>
      )}

      {firstChapter && (
        <div className={cn(styles['excerpt'])}>
          <h2 className={cn(styles['chapterTitle'])}>{firstChapter.title}</h2>
          <ManuscriptBodyHtml html={excerptOf(firstChapter.body)} className={cn(styles['chapterBody'])} />
        </div>
      )}

      <CommentThread shareId={view.share.id} viewerUserId={viewerUserId} viewerRole={viewerRole} className={cn(styles['comments'])} />

      {canModerate && (
        <TakedownDialog
          open={takedownOpen}
          onOpenChange={setTakedownOpen}
          shareId={view.share.id}
          targetType="share"
          targetId={view.share.id}
          assetName={view.title}
          {...(onTakenDown ? { onRemoved: onTakenDown } : {})}
        />
      )}
    </article>
  );
}
